import { EmptyState, Skeleton, cn, readerTimezone } from '@h2hspark/ui';
import { Mail, MessageCircle } from 'lucide-react';
import * as React from 'react';
import { Link, Outlet } from 'react-router-dom';

import { useMyConversations } from './api';
import {
  MESSAGES_INTRO,
  MESSAGES_TITLE,
  NEW_PILL_LABEL,
  NO_MESSAGES_DESCRIPTION,
  NO_MESSAGES_TITLE,
  NO_PREVIEW_YET,
  conversationRowLabel,
  initialsOf,
  messageStamp,
  personName,
  threadPath,
} from './constants';
import type { Conversation } from './types';

/**
 * The Messages page — `Web App.dc.html` §2.9, `MEM-055` AC-2 and AC-7.
 *
 * A heading, its sentence, and one row per thread, most recently active first. The order is the
 * server's; this page does not sort, because two places that decide an order are two places that
 * can disagree about it.
 *
 * ## The thread opens through `<Outlet/>`
 *
 * `/app/messages/:connectionRef` is a child route and `ThreadDialog` renders it — a dialog on
 * desktop over this list, full screen on a phone. The list stays mounted behind it, which is what
 * keeps `MEM-042` AC-6's *"back returns to the list at its previous scroll position"* true.
 *
 * ## The prototype's unread rule is backwards, and this is the correction
 *
 * `MEM-055`'s Notes: in `Web App.dc.html` the *New* pill and the Messages dot are raised by the
 * member's **own** sent opener, and a thread with no reply shows a blank preview line. Unread
 * here is a message **received** and not opened (AC-3), and the preview is the latest message
 * whoever wrote it — prefixed *You:* where it is the member's own, so a row cannot read as though
 * somebody replied when nobody has.
 */
export function MemberMessagesPage(): React.ReactElement {
  const query = useMyConversations({ enabled: true });
  const reader = React.useMemo(() => readerTimezone(), []);
  const conversations = query.data?.conversations ?? [];

  return (
    <div>
      <header className="mb-7">
        <h1 className="mb-2 text-2xl tracking-tight md:text-4xl">{MESSAGES_TITLE}</h1>
        <p className="max-w-[620px] text-md text-muted-foreground md:text-lg">{MESSAGES_INTRO}</p>
      </header>

      {query.isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-[84px] w-full rounded-lg" />
          <Skeleton className="h-[84px] w-full rounded-lg" />
        </div>
      ) : conversations.length === 0 ? (
        <EmptyState
          icon={<Mail className="h-6 w-6" aria-hidden />}
          title={NO_MESSAGES_TITLE}
          description={NO_MESSAGES_DESCRIPTION}
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {conversations.map((conversation) => (
            <li key={conversation.connectionRef}>
              <ConversationRow conversation={conversation} reader={reader} />
            </li>
          ))}
        </ul>
      )}

      <Outlet />
    </div>
  );
}

/**
 * One row — §3.13.
 *
 * A 48px avatar; the name, the event and the stamp on one line; the *New* pill pushed right; the
 * preview beneath. `purple-50` behind an unread row, which is the prototype's own tint.
 *
 * The whole row is one link, so there is one focus stop and one tap target — a phone row with a
 * separate avatar link and name link is two 48px targets 4px apart.
 */
function ConversationRow({
  conversation,
  reader,
}: {
  readonly conversation: Conversation;
  readonly reader: string;
}): React.ReactElement {
  const unread = conversation.unreadCount > 0;
  const stamp = messageStamp(conversation.lastMessageAt, reader);
  const preview =
    conversation.preview === null
      ? NO_PREVIEW_YET
      : conversation.lastMessageIsMine
        ? `You: ${conversation.preview}`
        : conversation.preview;

  return (
    <Link
      to={threadPath(conversation.connectionRef)}
      aria-label={conversationRowLabel(conversation)}
      className={cn(
        'flex items-start gap-3.5 rounded-lg border border-border-subtle p-4 shadow-xs transition-colors',
        unread ? 'bg-brand-50' : 'bg-card hover:bg-ivory-100',
      )}
    >
      <span
        className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-sm font-extrabold text-brand-700"
        aria-hidden
      >
        {initialsOf(conversation)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
          <span className="font-brand text-md font-bold text-foreground">
            {personName(conversation)}
          </span>
          {conversation.eventName === null ? null : (
            <span className="truncate text-xs text-text-subtle">· {conversation.eventName}</span>
          )}
          {stamp === null ? null : (
            <span className="whitespace-nowrap text-xs text-text-subtle">· {stamp}</span>
          )}
          {unread ? (
            <span className="ml-auto inline-flex items-center gap-1 rounded-pill bg-brand-accent px-2.5 py-[3px] font-brand text-2xs font-bold text-white">
              <Mail className="h-3 w-3" aria-hidden />
              {NEW_PILL_LABEL}
            </span>
          ) : null}
        </span>
        <span className="mt-1 flex items-center gap-1.5">
          <MessageCircle className="h-3.5 w-3.5 shrink-0 text-text-subtle" aria-hidden />
          <span className="truncate text-sm text-muted-foreground">{preview}</span>
        </span>
      </span>
    </Link>
  );
}
